// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { ensureUser } from '../../util/data';
import { getConditionStyle } from '../../util/listingConditions';
import { displayBrand, findEnumLabel } from '../../util/listingFieldLabels';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  Avatar,
  FavoriteButton,
  ListingCardThumbnail,
  NamedLink,
  PartnerPhotoMark,
  ResponsiveImage,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  // Prefer Sharetribe's built-in `scaled-*` variants (generated with
  // imgix `fit=clip` — the full photo, no crop) and let the CSS
  // `object-fit: cover` on .rootForImage do the cropping instead, since
  // CSS always crops from the CENTRE. The custom `${variantPrefix}`
  // variants are generated with `crop=edges` (imgix edge detection),
  // which picks an off-centre region and made garments look shifted to
  // one side. Falls back to the prefixed variants when a page hasn't
  // requested the scaled ones, so nothing breaks.
  const allVariants = firstImage?.attributes?.variants || {};
  const scaledVariants = Object.keys(allVariants).filter(k => k.startsWith('scaled-'));
  const variants =
    scaledVariants.length > 0
      ? scaledVariants
      : Object.keys(allVariants).filter(k => k.startsWith(variantPrefix));

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard — ZUPRIZE product card.
 *
 * Layout (matches Figma ProductCard):
 * - Image on top (square aspect ratio), rounded card with subtle shadow.
 * - Info section below: title + price on one row, optional size + condition
 *   badge, optional brand, optional "Sold by …" line with seller avatar.
 *
 * Each meta row is rendered conditionally — listings without a given
 * extended-data field collapse the row gracefully so the card stays tight.
 *
 * The favorite (heart) icon shown in the Figma is intentionally omitted
 * until the favorites feature ships; add it here when wiring that up.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing API entity: listing or ownListing (denormalized)
 * @param {string?} props.className extra class on the root link
 * @param {string?} props.rootClassName overwrite the root class entirely
 * @param {string?} props.aspectRatioClassName custom class on the image's aspect wrapper
 * @param {boolean} [props.darkMode] toggles light text — preserved for PageBuilder consumers
 * @param {string?} props.renderSizes for img srcset
 * @param {Function?} props.setActiveListing hover handler (used by SearchPage map)
 * @param {boolean} [props.showAuthorInfo=true]
 * @param {boolean} [props.showPartnerMark=true] the Z on a verified partner's photo
 * @param {boolean} [props.lazyLoadImage=true]
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    showPartnerMark = true,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Read meta fields straight from publicData. Field keys mirror the entries
  // in `src/config/configListing.js`. Enum values (`merke`, `tilstand`) are
  // translated to their display labels via the listing fields config.
  const listingFields = config.listing.listingFields;
  const sizeRaw = publicData?.['storrelse-klar'] || publicData?.['storrelse-sko'] || null;
  const conditionLabel = findEnumLabel(listingFields, 'tilstand', publicData?.tilstand);
  const conditionStyle = getConditionStyle(publicData?.tilstand);
  const brandLabel = displayBrand(listingFields, publicData);
  // Sizes use display-friendly option keys (XS, 38, …) so we render the value
  // directly. Uppercase keeps letter sizes consistent (xs → XS).
  const sizeLabel = sizeRaw ? String(sizeRaw).toUpperCase() : null;

  const author = ensureUser(listing?.author);
  const authorDisplayName = author?.attributes?.profile?.displayName;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const showMetaRow = sizeLabel || conditionLabel;
  const showAuthor = showAuthorInfo && authorDisplayName;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      <div className={css.imageWrapper}>
        {showListingImage ? (
          <ListingCardImage
            renderSizes={renderSizes}
            title={titlePlain}
            listing={listing}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
            aspectRatioClassName={aspectRatioClassName}
            lazyLoadImage={lazyLoadImage}
          />
        ) : (
          <ListingCardThumbnail
            style={cardStyle}
            listingTitle={title}
            className={aspectRatioClassName}
            width={aspectWidth}
            height={aspectHeight}
            setActivePropsMaybe={setActivePropsMaybe}
          />
        )}
        {/* Guro's Z on a verified partner's photo, read from the author the
            listing query already includes (see PARTNER_USER_FIELDS). */}
        {showPartnerMark ? <PartnerPhotoMark user={author} className={css.partnerMark} /> : null}
        {id ? <FavoriteButton listingId={id} className={css.favoriteButton} /> : null}
      </div>

      <div className={css.info}>
        <div className={css.titleRow}>
          {showListingImage ? (
            <p className={classNames(css.title, { [css.lightText]: darkMode })} title={titlePlain}>
              {titleFormatted}
            </p>
          ) : null}
          {/* Under the title at every width, so the one-line title gets the
              card's full width, and at the same height on every card in a
              row: it stays with the title rather than with the size, brand
              and seller lines, which sit at the bottom (Guro, 29 Oct). */}
          {showPrice ? (
            <p className={css.price} title={priceTooltip}>
              {priceMessage}
            </p>
          ) : null}
        </div>

        <div className={css.infoBody}>
          {showMetaRow ? (
            <div className={css.metaRow}>
              {sizeLabel ? <p className={css.size}>{sizeLabel}</p> : <span />}
              {conditionLabel ? (
                <span className={css.condition} style={conditionStyle || undefined}>
                  {conditionLabel}
                </span>
              ) : null}
            </div>
          ) : null}

          {brandLabel ? <p className={css.brand}>{brandLabel}</p> : null}

          {showAuthor ? (
            <div className={css.author}>
              <Avatar className={css.authorAvatar} user={author} disableProfileLink />
              <p className={classNames(css.authorName, { [css.lightText]: darkMode })}>
                <FormattedMessage
                  id="ListingCard.soldBy"
                  values={{
                    name: <span className={css.authorNameHighlight}>{authorDisplayName}</span>,
                  }}
                />
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
